/* eslint-disable @typescript-eslint/no-explicit-any */
import axiosInstance from '@/config/axios';
import { ApiResource, ApiResourceItem, ApiResources } from '@/types/admin-api';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '..';

interface Params {
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  page: number;
  limit: number;
  title: string;
}

const adminQueryKeys = {
  all: <R extends ApiResource>(resource: R) => [resource] as const,
  lists: <R extends ApiResource>(resource: R) => [resource, 'list'] as const,
  list: <R extends ApiResource>(resource: R, params?: Params) =>
    [resource, 'list', params] as const,
  detail: <R extends ApiResource>(resource: R, id: string) => [resource, 'detail', id] as const,
};

export const useGetList = <R extends ApiResource, T = ApiResources[R]>(
  resource: R,
  params?: Params,
) => {
  type DataType = ApiResources[R];

  return useQuery<DataType, Error, T>({
    queryKey: adminQueryKeys.list(resource, params),
    queryFn: async () => {
      const { data } = await axiosInstance.get<DataType>(`/${resource}`, { params });
      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetOne = <R extends ApiResource, T = ApiResourceItem<R>>(
  resource: R,
  id: string,
  options?: { enabled?: boolean },
) => {
  return useQuery<T, Error, T>({
    queryKey: adminQueryKeys.detail(resource, id),
    queryFn: async () => {
      const { data } = await axiosInstance.get<T>(`/${resource}/${id}`);
      return data;
    },
    enabled: options?.enabled ?? true,
  });
};

export const useUpdateOne = <R extends ApiResource, T = ApiResourceItem<R>>(resource: R) => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<any> }) => {
      if (resource === 'applications') {
        const formData = prepareApplicationFormData(data);

        const response = await axiosInstance.put(`/${resource}/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return { data: response.data };
      }
      if (resource === 'chats' || resource === 'chat-templates') {
        if (!data.avatar?.rawFile && !data.avatar?.url) {
          await axiosInstance.delete(`/${resource}/${id}/avatar`);
        }
        const formData = prepareChatFormData(data);
        const response = await axiosInstance.put(`/${resource}/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return { data: response.data };
      }
      return await axiosInstance.put<T>(`/${resource}/${id}`, data);
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.lists(resource) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.detail(resource, id), exact: true });
    },
  });
};

export const useCreateOne = <R extends ApiResource, T = ApiResourceItem<R>>(resource: R) => {
  return useMutation({
    mutationFn: async ({ data }: { data: Partial<any> }) => {
      if (resource === 'applications' && data.iconLink?.rawFile instanceof File) {
        const formData = prepareApplicationFormData(data);

        const response = await axiosInstance.post(`/${resource}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return { data: response.data };
      }
      if (resource === 'chats' || resource === 'chat-templates') {
        const formData = prepareChatFormData(data);
        const response = await axiosInstance.post(`/${resource}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return { data: response.data };
      }
      return await axiosInstance.post<T>(`/${resource}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.lists(resource) });
    },
  });
};

export const useDeleteOne = <R extends ApiResource, T = ApiResourceItem<R>>(resource: R) => {
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data: response } = await axiosInstance.delete<T>(`/${resource}/${id}`);
      return response;
    },
    onSuccess: (_data, { id }) => {
      queryClient.removeQueries({ queryKey: adminQueryKeys.detail(resource, id), exact: true });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.lists(resource) });
    },
  });
};

export const useDeleteMany = <R extends ApiResource, T = ApiResourceItem<R>>(resource: R) => {
  return useMutation({
    mutationFn: async ({ ids }: { ids: string[] }) => {
      const { data: response } = await axiosInstance.delete<T>(`/${resource}`, {
        params: { ids: ids.join(',') },
      });
      return response;
    },
    onSuccess: (_data, { ids }) => {
      ids.forEach((id) => {
        queryClient.removeQueries({ queryKey: adminQueryKeys.detail(resource, id), exact: true });
      });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.lists(resource) });
    },
  });
};

function prepareApplicationFormData(data: any) {
  const formData = new FormData();

  Object.keys(data).forEach((key) => {
    if (key === 'iconLink') return;

    const value = data[key];
    if (value === undefined) return;
    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else if (value !== null) {
      formData.append(key, String(value));
    }
  });

  if (data.iconLink?.rawFile) {
    formData.append('file', data.iconLink.rawFile);
  } else if (data.iconLink?.url) {
    formData.append('iconLink', data.iconLink.url);
  }

  return formData;
}

function prepareChatFormData(data: any) {
  const formData = new FormData();

  Object.keys(data).forEach((key) => {
    if (key === 'avatar') return;

    const value = data[key];
    if (value === undefined) return;

    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
      return;
    }

    if (value !== null) {
      formData.append(key, String(value));
    }
  });

  if (data.avatar?.rawFile) {
    formData.append('avatar', data.avatar.rawFile);
  }
  return formData;
}
